import React from 'react';
import { formatPrice, getTrans } from '@/utils/tools';
import { cdnFile } from '@/utils/cdnFile';
import FailToLoad from '@/assets/img/fail_to_load.svg';
import UIOptimisationOrderDefaultImg from '@/assets/img/ui-optimisation-recommender/ui-optimisation-order-default-img.svg';
import clsx from 'classnames';
import { AgentUploadLoading } from '@/components/Common/Loader';
import SingleButton from '@/components/SingleButton';
import imageFailure from '@/assets/img/image_failure.svg';
import {
  GetMsgResCode,
  TRACKING_MSG_TYPE,
  ITEMS_TEXT,
  ITEM_TEXT,
  TOTAL_TEXT,
  SELECT_TEXT,
  OrderCardType,
  REFUND_AMOUNT_TEXT,
} from '@/utils/constants';
import { postChatMsg, reviewChatEndText } from '@/utils/agent';
import { observer, inject } from 'mobx-react';
import { TRACKING_TYPE } from '@/utils/tracking/constants';
import { tracker } from '@/utils/tracking';
import { addObserver, removeObserver } from '@/utils/intersectionObserver';
import { getChildInject, bridgeCallFunc } from '@/utils/instantLoaded/tools';
import { handleOrderRedirect } from '@/utils/btnClickHandler';
import reporter from '@/utils/report';
import { orderCardStyleMap } from '../OrderList';
import getOrderMilestone from '@/components/ChatbotUI/OrderCardWithButton/getOrderMilestone';

import './style.scss';
import FoodyOrderCard, { OrderType } from './OrderContent/FoodyOrderCard';
const PREFIX = 'order-container';
@tracker
@inject(getChildInject('agentStore', 'userStore', 'initStore'))
@observer
export default class Order extends React.PureComponent {
  static defaultProps = {
    // props
    order_id: '',
    title: '',
    price: 0,
    image: '',
    specification: '',
    item_count: 1,
    item_amount: 1,
    items: [],
    type_name: '',
    isOrderCard: false,
    isRR: false,
    showCopyBox: true,
    // handleClick: () => {},
    // stores
    sessionStore: {},
    useUIOptimisationHotQuestion: false,
    // uri_key: '',
  };

  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      priceIsMultiline: false,
      orderInfoIsMultiline: false,
    };
    const { userStore, initStore, agentStore, isHistory, order_id, order_list_type } = props;

    this.orderMessageRef = React.createRef();
    this.resendMessageRef = React.createRef();
    this.priceTextRef = React.createRef();
    this.orderInfoTextRef = React.createRef();

    this.trackingnameForOrderMessage = TRACKING_TYPE.ORDER_MESSAGE;

    this.trackingnameForResendOrder = TRACKING_TYPE.RESEND_ORDER;
    // 埋点数据，目前只在agent中的order有埋点
    this.trackingdata = {
      client_rank: userStore?.loyaltyTierCode?.toString() || '',
      current_chat_session_id: initStore?.sessionId,
      live_chat_session_id: agentStore?.connection?.sessionId,
      enquiry_type: agentStore?.enquiryType,
      account_type: userStore?.account_type,
      orderid: order_id,
      order_status: order_list_type,
      if_history: isHistory ? TRACKING_MSG_TYPE.HISTORY : TRACKING_MSG_TYPE.REAL_TIME,
    };
  }

  handleError = () => {
    this.setState({ hasError: true });
  };

  copyTracingNumber = (value) => {
    const params = { text: String(value) };
    bridgeCallFunc(() => {
      window.bridgeCallHandler('copyTextToClipboard', params);
    });
  };

  handleStatusClick = async (element) => {
    // 阻止冒泡
    element.stopPropagation();
    const { error, curPos, agentStore, params, tracker } = this.props;

    tracker.click({
      trackingname: this.trackingnameForResendOrder,
      data: { ...this.trackingdata },
    });

    if (error) {
      agentStore.UPDATE_AGENT_DATA_ITEM({ loading: true }, curPos);

      if (agentStore.agentEndedReason) {
        // 回显agent离开文案
        reviewChatEndText();
        agentStore.UPDATE_AGENT_DATA_ITEM({
          loading: false,
          error: true,
        }, curPos);
        return;
      }
      params.amount = params?.item_amount || 1;
      // 用户主动重发数
      reporter.apiBatchReport({ type: 'count', value: { label: 'channel_chat_message_user_resend_total' } });
      const { code: resCode } = await postChatMsg({
        type: 'ORDER',
        params,
      });

      if (resCode === GetMsgResCode.SUCCESS) {
        agentStore.UPDATE_AGENT_DATA_ITEM({
          loading: false,
          error: false,
        }, curPos);
      } else {
        agentStore.UPDATE_AGENT_DATA_ITEM({
          loading: false,
          error: true,
        }, curPos);
      }
    }
  };

  handleOrderClick = () => {
    const { handleClick, shouldRedirect } = this.props;
    if (handleClick) {
      handleClick();
    } else if (shouldRedirect) {
      handleOrderRedirect(this.props);
    }
  };

  componentDidUpdate() {
    const { error, loading, agentStore } = this.props;
    agentStore.inAgent && loading && removeObserver(this.resendMessageRef.current);
    agentStore.inAgent && error && addObserver(this.resendMessageRef.current);
    agentStore.inAgent && !error && !loading && addObserver(this.orderMessageRef.current);
  }

  componentDidMount() {
    if (this.priceTextRef?.current) {
      // 记录 ui 优化 price 是单行还是多行
      const lineHeight = parseFloat(getComputedStyle(this.priceTextRef.current).lineHeight);
      const textHeight = this.priceTextRef.current.clientHeight;
      if (textHeight > lineHeight) {
        this.setState({ priceIsMultiline: true });
      } else {
        this.setState({ priceIsMultiline: false });
      }
    }
    if (this.orderInfoTextRef?.current) {
      // 记录 ui 优化 info status 是单行还是多行
      const lineHeight = parseFloat(getComputedStyle(this.orderInfoTextRef.current).lineHeight);
      const textHeight = this.orderInfoTextRef.current.clientHeight;
      // 这个会有 icon 撑着 所以高一点
      if (textHeight >= (lineHeight * 2)) {
        this.setState({ orderInfoIsMultiline: true });
      } else {
        this.setState({ orderInfoIsMultiline: false });
      }
    }
  }

  componentWillUnmount() {
    removeObserver(this.resendMessageRef.current);
    removeObserver(this.orderMessageRef.current);
  }

  renderOrderDetail() {
    const {
      price,
      specification,
      tracing_number,
      delivered_by,
      item_amount,
      isOrderCard,
      item_count, // 第一个商品的数量
      total_item_count, // 全部商品的数量
      total_item_price,
      isRR,
      return_status_desc,
      return_items_count,
      refund_amount,
      order_sn,
      userStore: { abTestingConfigs },
      showCopyBox,
      orderCardType = OrderCardType.oldCarousel,
      status,
    } = this.props;
    const testGroup = abTestingConfigs['bot.order_selection_ui'] === 'auto_popup';
    const itemText = item_count > 1 ? getTrans(ITEMS_TEXT) : getTrans(ITEM_TEXT);
    const rrItemText = return_items_count > 1 ? getTrans(ITEMS_TEXT) : getTrans(ITEM_TEXT);
    const totalItemText = total_item_count > 1 ? getTrans(ITEMS_TEXT) : getTrans(ITEM_TEXT);

    if (isRR) {
      return (
        <>
          <div className="price-wrap">
            <div className="count">
              {
                return_items_count
                  ? `${return_items_count} ${rrItemText}`
                  : `${item_count} ${itemText}`
              }
            </div>
            <div className="price">{formatPrice(refund_amount)}</div>
          </div>
          {return_status_desc && <span className="order-status rr-status">{return_status_desc}</span>}
          {showCopyBox && (
            <div className="tracing-number-box">
              <span className="tracing-number">{order_sn}</span>
              <span className="copy" onClick={() => this.copyTracingNumber(order_sn)}>
                {getTrans('copy_text')}
              </span>
            </div>
          )}
        </>
      );
    }

    if (isOrderCard) { // shipping info page进来会有一个order card的卡片
      return (
        <>
          <div className="delivered">{`${getTrans('delivered_by_text')}: ${delivered_by}`}</div>
          {
            Boolean(tracing_number)
            && <div className="tracing-number-box">
              <span className="tracing-number">{tracing_number}</span>
              <span className="copy" onClick={() => this.copyTracingNumber(tracing_number)}>
                {getTrans('copy_text')}
              </span>
            </div>
          }
        </>
      );
    }

    if (testGroup) {
      return (
        <>
          <div className="specification-wrap">
            <div className="specification">{specification}</div>
            <div className="amount">X{item_amount}</div>
          </div>
          <div className="price-wrap">
            <div className="count">{item_count} {itemText}</div>
            <div className="price">{formatPrice(price)}</div>
          </div>
        </>
      );
    }

    if (orderCardType === OrderCardType.oldCarousel) {
      return (
        <>
          <div className="specification">{specification}</div>
          <div className="price">{formatPrice(price)}</div>
        </>
      );
    }

    return (
      <>
        <div className="order-info-detail">
          <div className="order-info-item">
            {' '}
            {total_item_count} {totalItemText}
          </div>
          <div className="order-info-price">
            {orderCardType !== OrderCardType.bigCarousel && (
              <span>{getTrans(TOTAL_TEXT)}:</span>
            )}{' '}
            {formatPrice(total_item_price)}
          </div>
        </div>

        <div className="order-info-wrap">
          <div className="order-info-status">{status}</div>
          {(orderCardType === OrderCardType.verticalList || orderCardType === OrderCardType.twoRowCarousel)
            && (
              <SingleButton className="order-info-select-btn" text={getTrans(SELECT_TEXT)} />
            )}
        </div>
      </>
    );
  }

  renderOrderInfo() {
    const {
      useUIOptimisationHotQuestion,
      title,
      type_name,
      isRR,
      userStore: { abTestingConfigs },
      item_count,
      total_price,
      order_status_desc,
      milestone,
      return_status_desc,
      refund_amount,
    } = this.props;
    const testGroup = abTestingConfigs['bot.order_selection_ui'] === 'auto_popup';
    const itemText = item_count > 1 ? getTrans(ITEMS_TEXT) : getTrans(ITEM_TEXT);
    const orderMilestone = getOrderMilestone({ milestone, useUIOptimisationHotQuestion, order: this.props });
    if (useUIOptimisationHotQuestion) {
      return <div className="order-ui-optimisation-hot-question">
        <div className="order-ui-optimisation-hot-question-name">{title}</div>
        <div className="order-ui-optimisation-hot-question-price" ref={this.priceTextRef}>
          {item_count} {itemText}{this.state.priceIsMultiline ? '' : ','}&nbsp;
          <span className="order-ui-optimisation-hot-question-price-span" style={{ display: this.state.priceIsMultiline ? 'block' : 'inline' }}>
            {isRR ? getTrans(REFUND_AMOUNT_TEXT) : getTrans(TOTAL_TEXT)}: {isRR ? formatPrice(refund_amount) : formatPrice(total_price)}
          </span>
        </div>
        <div className="order-ui-optimisation-hot-question-milestone">
          {orderMilestone && !isRR ? (<>
            <div>
              {orderMilestone?.icon}
            </div>
            <div ref={this.orderInfoTextRef} className="order-ui-optimisation-hot-question-milestone-text order-ui-optimisation-hot-question-milestone-text-no-clamp">
              {orderMilestone?.text}
            </div>
          </>) : (
            <div ref={this.orderInfoTextRef} className="order-ui-optimisation-hot-question-milestone-text">
              {isRR ? return_status_desc : order_status_desc}
            </div>
          )}
        </div>
      </div>;
    };
    return <div className={clsx('order-info', { 'rr-info': isRR })}>
      <div className="name">{title}</div>
      {this.renderOrderDetail()}
      {!isRR && type_name && testGroup && <span className="order-status">{type_name}</span>}
    </div>;
  }

  renderShopeeOrder() {
    const {
      image,
      useUIOptimisationHotQuestion,
    } = this.props;
    const { hasError, priceIsMultiline, orderInfoIsMultiline } = this.state;
    const imgErrorSrc = useUIOptimisationHotQuestion ? UIOptimisationOrderDefaultImg : FailToLoad;

    return (
      <>
        <div className={clsx('order-image', { 'order-image-big': useUIOptimisationHotQuestion && (priceIsMultiline || orderInfoIsMultiline) })}>
          <img className={clsx({ error: hasError })} onError={this.handleError} src={hasError ? imgErrorSrc : cdnFile(image)} />
        </div>
        {this.renderOrderInfo()}
      </>
    );
  }


  renderOrder() {
    const { order_type } = this.props;
    // 渲染不同的order组件
    if (order_type && order_type !== OrderType.Shopee) {
      return (
        <FoodyOrderCard orderMsg={this.props} />
      );
    }
    return this.renderShopeeOrder();
  }

  render() {
    const {
      loading,
      error,
      messageId,
      isRR,
      userStore: { abTestingConfigs },
      showCopyBox,
      orderCardType = OrderCardType.oldCarousel,
      useUIOptimisationHotQuestion,
    } = this.props;
    const { hasError, priceIsMultiline, orderInfoIsMultiline } = this.state;

    const testGroup = abTestingConfigs['bot.order_selection_ui'] === 'auto_popup';

    const trackingdata = JSON.stringify({
      message_id: messageId,
      ...this.trackingdata,
    });

    const getHeight = () => {
      if (useUIOptimisationHotQuestion) {
        return 'auto';
      }
      return (isRR && showCopyBox) || testGroup ? '134px' : orderCardType === OrderCardType.oldCarousel ? '104px' : 'auto';
    };

    const imgErrorSrc = useUIOptimisationHotQuestion ? UIOptimisationOrderDefaultImg : FailToLoad;

    return <div className={clsx(`${PREFIX} ${orderCardStyleMap[orderCardType]} chatbot-order-container`, { 'ui-optimisation-order-container': useUIOptimisationHotQuestion })}
      style={{ height: getHeight() }
      }
      onClick={this.handleOrderClick}
      ref={this.orderMessageRef}
      data-trackingname={this.trackingnameForOrderMessage}
      data-trackingdata={trackingdata}
    >
      {
        loading
          ? <AgentUploadLoading />
          : <div className="status-icon"
            onClick={(e) => this.handleStatusClick(e)}
            ref={this.resendMessageRef}
            data-trackingname={this.trackingnameForResendOrder}
            data-trackingdata={JSON.stringify(this.trackingdata)}
          >
            {error && <img src={imageFailure} />}
          </div>
      }
      {this.renderOrder()}
    </div>;
  }
}
